import { xml2js } from 'xml-js';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { from } from 'rxjs';
import { forkJoin } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { UnitMeasurement } from '../../../../assets/data/enum/unit_measurement.enum';
import { Crop, Equipment, Farm, Field, FieldActivity, Product, User } from '../../_models';
import { MapShape } from '../../_models/mapShape';
import { decodeDDI, js2xml } from './ISOXMLManager/utils';
import {
  DATA_LOG_TRIGGER_METHODS,
  DDI,
  PRODUCT_TYPES,
  TAGS,
  TASK_STATUSES,
  TIME_TYPES,
} from './ISOXMLManager/constants';
import PGNs from './ISOXMLManager/PGNs';
import { EntitiesManager } from './ISOXMLManager/entitiesManager';
import {
  CulturalPracticeEntity,
  PartfieldEntity,
  TaskEntity,
  TimeEntity,
  ValuePresentationEntity,
  WorkerEntity,
  ProcessDataVariableEntity,
  FarmEntity,
  ProductEntity,
  DeviceEntity,
} from './ISOXMLManager/entities';
import { CropTypeEntity } from './ISOXMLManager/entities/CropType';
import { CropVarietyEntity } from './ISOXMLManager/entities/CropVariety';
import { EquipmentService } from '../equipment.service';
import { UploadService } from '../upload.service';
import { ProductRelationEntity } from './ISOXMLManager/entities/ProductRelation';
import { DataLogTriggerEntity } from './ISOXMLManager/entities/DataLogTrigger';
import { ThemeConfig } from '../../../../assets/data/enum/themeConfig';

const SIZE_UNIT_MULTIPLIER = {
  ha: 10000,
  m2: 1,
};

const XFARM_UNTIS = {
  [UnitMeasurement.UNIT]: { scale: 1, DDI: DDI.SetpointCountContent },
  [UnitMeasurement.KG_HA]: { scale: 100, DDI: DDI.SetpointMassPerAreaApplicationRate },
  [UnitMeasurement.L_HA]: { scale: 10000, DDI: DDI.SetpointVolumePerAreaApplicationRate },
  [UnitMeasurement.L_S]: { scale: 1000000, DDI: DDI.SetpointVolumePerTimeApplicationRate },
  [UnitMeasurement.L_MIN]: { scale: 60000000, DDI: DDI.SetpointVolumePerTimeApplicationRate },

  // We need these because sometimes (always?) the units are stored in DB as translations
  unit: { scale: 1, DDI: DDI.SetpointCountContent },
  'kg/ha': { scale: 100, DDI: DDI.SetpointMassPerAreaApplicationRate },
  'l/ha': { scale: 10000, DDI: DDI.SetpointVolumePerAreaApplicationRate },
  'l/s': { scale: 1000000, DDI: DDI.SetpointVolumePerTimeApplicationRate },
  'l/min': { scale: 60000000, DDI: DDI.SetpointVolumePerTimeApplicationRate },
};

const XFARM_PRODUCT_DDIS = {
  [UnitMeasurement.UNIT]: DDI.ActualCountContent,
  [UnitMeasurement.KG_HA]: DDI.ActualMassContent,
  [UnitMeasurement.L_HA]: DDI.ActualVolumeContent,
  [UnitMeasurement.L_S]: DDI.ActualVolumeContent,
  [UnitMeasurement.L_MIN]: DDI.ActualVolumeContent,

  // We need these because sometimes (always?) the units are stored in DB as translations
  unit: DDI.ActualCountContent,
  'kg/ha': DDI.ActualMassContent,
  'l/ha': DDI.ActualVolumeContent,
  'l/s': DDI.ActualVolumeContent,
  'l/min': DDI.ActualVolumeContent,
};

const { apiBaseUrl } = environment;

function farmToFarmEntity(farm: Farm): FarmEntity {
  return new FarmEntity(
    farm.id.toString(),
    farm.name ? farm.name.toString().slice(0, 32) : '',
    farm.street1 ? farm.street1.toString().slice(0, 32) : null,
    null,
    farm.zipCode ? farm.zipCode.toString().slice(0, 10) : null,
    farm.city ? farm.city.toString().slice(0, 32) : null,
    null,
    farm.country ? farm.country.toString().slice(0, 32) : null
  );
}

function cropToCropVarietyEntity(crop: Crop): CropVarietyEntity {
  if (!crop.variety) {
    return null;
  }
  return new CropVarietyEntity(
    crop.variety.id.toString(),
    crop.variety.name.toString().slice(0, 32)
  );
}

function cropToCropTypeEntity(translate: TranslateService, crop: Crop): CropTypeEntity {
  const variety = cropToCropVarietyEntity(crop);
  return new CropTypeEntity(
    crop.commodity.id.toString(),
    translate
      .instant(('select_commodity.' + crop.commodity.name) as string)
      .toString()
      .slice(0, 32),
    variety ? [variety] : []
  );
}

function fieldToFieldEntity(
  translate: TranslateService,
  field: Field,
  farm?: Farm,
  crop?: Crop
): PartfieldEntity {
  return new PartfieldEntity(
    +field.id,
    field.name.slice(0, 32),
    Math.round(+field.size * SIZE_UNIT_MULTIPLIER[field.sizeUnit]),
    field.polygon,
    farm ? farmToFarmEntity(farm) : null,
    crop ? cropToCropTypeEntity(translate, crop) : null,
    crop ? cropToCropVarietyEntity(crop) : null
  );
}

export function fieldEntityToField(fieldEntity: PartfieldEntity): any {
  return {
    name: fieldEntity.name,
    geoJsonArea: JSON.stringify({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: fieldEntity.boundary,
        },
      ],
    }),
    size: fieldEntity.area / SIZE_UNIT_MULTIPLIER['ha'],
    sizeUnit: 'ha',
  };
}

function activityToTimeEntities(activity: FieldActivity) {
  if (activity.plannedPeriodFrom) {
    return [
      new TimeEntity(TIME_TYPES.Planned, +activity.plannedPeriodFrom, +activity.plannedPeriodTo),
    ];
  } else if (activity.activityDate) {
    return [new TimeEntity(TIME_TYPES.Planned, +activity.activityDate)];
  } else {
    return [];
  }
}

const FMIS_TITLE = ThemeConfig.textName;
const FMIS_URI = 'https://app.xfarm.ag/';

@Injectable()
export class ISOXMLService {
  constructor(
    private translate: TranslateService,
    private equipmentService: EquipmentService,
    private uploadService: UploadService,
    private http: HttpClient
  ) {}

  private activityToTaskEntity(
    activity: FieldActivity,
    field: Field,
    users: User[],
    products: Product[],
    mapShape?: MapShape,
    farm?: Farm,
    crop?: Crop,
    tractor?: Equipment
  ): Observable<TaskEntity> {
    const workers = activity.users.map((activityUser) => {
      const user = users.find((user) => user.id === activityUser.id);
      return new WorkerEntity(
        +user.id,
        (user.firstName as string).slice(0, 32),
        (user.lastName as string).slice(0, 32)
      );
    });
    const taskEntityId = field ? `${activity.id}--${field.id}` : `${activity.id}`;

    const productRelations = [];
    let totalDose = 0;
    const xFarmUnits = new Set<string>();
    const activityProducts: ProductEntity[] = [];
    (activity.mixes || []).forEach((mix) => {
      mix.doseUnit && xFarmUnits.add(mix.doseUnit as string);
      const ddi = XFARM_PRODUCT_DDIS[mix.doseUnit as string] || DDI.ActualMassContent;
      const product = new ProductEntity(
        mix.product ? mix.product.id.toString() : null,
        mix.product
          ? (mix.product.name || (mix.product as any).itemName).toString().slice(0, 32)
          : 'Product',
        ddi
      );
      productRelations.push(new ProductRelationEntity(product, mix.dose as number));
      activityProducts.push(product);
      totalDose += mix.dose as number;
    });
    if (activity.volume && activity.distributionMethod != 'fertirrigation') {
      const waterProduct = new ProductEntity(
        `${taskEntityId}--water`,
        'Water',
        DDI.ActualVolumeContent
      );
      productRelations.push(new ProductRelationEntity(waterProduct, activity.volume as number));
      activityProducts.push(waterProduct);
      totalDose += activity.volume as number;
      xFarmUnits.add(UnitMeasurement.L_HA);
    }

    let mainActivityProduct = null;
    if (activityProducts.length <= 1) {
      mainActivityProduct = activityProducts[0];
    } else {
      const isAnyLiquid = !!activityProducts.find(
        (product) => product.quantityDDI === DDI.ActualVolumeContent
      );
      mainActivityProduct = new ProductEntity(
        `${taskEntityId}--mix`,
        'Mix product',
        isAnyLiquid ? DDI.ActualVolumeContent : DDI.ActualMassContent,
        PRODUCT_TYPES.Mixture,
        totalDose,
        productRelations
      );
    }

    let xFarmUnit = '';

    if (xFarmUnits.size <= 1) {
      xFarmUnit = Array.from(xFarmUnits)[0] || 'kg/ha';
    } else {
      if (
        Array.from(xFarmUnits).find(
          (unit) => XFARM_UNTIS[unit].DDI === DDI.SetpointVolumePerAreaApplicationRate
        )
      ) {
        xFarmUnit = 'l/ha';
      } else if (
        Array.from(xFarmUnits).find(
          (unit) => XFARM_UNTIS[unit].DDI === DDI.SetpointMassPerAreaApplicationRate
        )
      ) {
        xFarmUnit = 'kg/ha';
      } else {
        xFarmUnit = 'unit';
      }
    }
    const unit = XFARM_UNTIS[xFarmUnit];

    const valuePresentation = new ValuePresentationEntity(
      xFarmUnit,
      0,
      1 / unit.scale,
      Math.ceil(Math.log10(unit.scale)),
      this.translate.instant('containers_type.' + xFarmUnit)
    );

    const rawEquipmentXmlRequest = tractor ? this.tractorToDeviceEntry(tractor) : from([null]);

    let prescriptionMap = null;
    if (mapShape) {
      const geoJSON = JSON.parse(mapShape.geoJson);
      prescriptionMap = {
        ...geoJSON,
        features: geoJSON.features.map((feature) => ({
          ...feature,
          properties: {
            ...feature.properties,
            DOSE: feature.properties.DOSE * unit.scale,
          },
        })),
      };
    }

    return rawEquipmentXmlRequest.pipe(
      map((deviceEntity) => {
        const dataLogTriggers = PGNs.map(
          (pgn) =>
            new DataLogTriggerEntity(
              decodeDDI(DDI.PGNBasedData),
              DATA_LOG_TRIGGER_METHODS.TimeInterval,
              pgn.PGN,
              pgn.StartBit,
              pgn.StopBit
            )
        );
        dataLogTriggers.push(
          new DataLogTriggerEntity(
            decodeDDI(DDI.RequestDefaultProcessData),
            DATA_LOG_TRIGGER_METHODS.TimeInterval |
              DATA_LOG_TRIGGER_METHODS.DistanceInterval |
              DATA_LOG_TRIGGER_METHODS.ThresholdLimits |
              DATA_LOG_TRIGGER_METHODS.OnChange |
              DATA_LOG_TRIGGER_METHODS.Total
          )
        );
        return new TaskEntity(
          taskEntityId,
          activity.done ? TASK_STATUSES.completed : TASK_STATUSES.planned,
          field ? fieldToFieldEntity(this.translate, field, farm, crop) : null,
          new CulturalPracticeEntity(
            activity.type,
            this.translate
              .instant('select_activity_2.' + activity.type)
              .toString()
              .slice(0, 32)
          ),
          activityToTimeEntities(activity),
          (field ? `${activity.id}--${field.id}` : `${activity.id}`).slice(0, 32),
          workers,
          prescriptionMap,
          mapShape || mainActivityProduct
            ? new ProcessDataVariableEntity(
                unit.DDI,
                totalDose * unit.scale,
                valuePresentation,
                mainActivityProduct
              )
            : null,
          farm ? farmToFarmEntity(farm) : null,
          deviceEntity ? [deviceEntity] : [],
          [],
          dataLogTriggers
        );
      })
    );
  }

  tractorToDeviceEntry(tractor: Equipment): Observable<any> {
    if (!tractor.agrirouterRawContentURL) {
      return from([null]);
    }

    return from(
      fetch(tractor.agrirouterRawContentURL as string)
        .then((response) => response.text())
        .then((xmlString) => {
          const xml = xml2js(xmlString, { compact: true, alwaysArray: true })[TAGS.Device][0];
          const deviceEntity = DeviceEntity.fromXML(xml, {}).entity;
          deviceEntity.id = tractor.id.toString();
          return deviceEntity;
        })
    );
  }

  uploadDeviceRawXml(device: DeviceEntity): Observable<string> {
    const entitiesManager = new EntitiesManager();
    const deviceXml = device.toXML('DVC0', entitiesManager);
    const xmlString = js2xml({ [TAGS.Device]: deviceXml }, { compact: true, spaces: 2 });
    const file = new File([xmlString], 'rawXMLContent.xml', { type: 'application/xml' });
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.uploadService.saveFile(formData).pipe(map((uploadInfo) => uploadInfo.imageUrl));
  }

  async parseISOXML(data: Blob, dataType: string): Promise<EntitiesManager> {
    const entitiesManager = new EntitiesManager();
    await entitiesManager.parseISOXMLFile(data, dataType, FMIS_URI);
    return entitiesManager;
  }

  fieldsToISOXML(fields: Field[], farm?: Farm) {
    const entitiesManager = new EntitiesManager();
    fields.forEach((field) => {
      entitiesManager.registerEntity(fieldToFieldEntity(this.translate, field, farm));
    });

    return entitiesManager.generateXML({ fmisTitle: FMIS_TITLE, fmisURI: FMIS_URI });
  }

  async ISOXMLFile2Fields(data: Blob, dataType: string) {
    const entitiesManager = new EntitiesManager();
    await entitiesManager.parseISOXMLFile(data, dataType, FMIS_URI);

    const fields = entitiesManager
      .getEntitiesOfType(TAGS.Partfield)
      .map((isoField) => fieldEntityToField(isoField as PartfieldEntity));

    return fields;
  }

  async ISOXMLToPrescriptionMaps(data: Blob) {
    const entitiesManager = new EntitiesManager();
    await entitiesManager.parseISOXMLFile(data, 'application/zip', FMIS_URI);

    const tasks = entitiesManager.getEntitiesOfType<TaskEntity>(TAGS.Task);

    return tasks.map((task) => task.prescriptionGeoJSON).filter((e) => e);
  }

  activitiesToISOXML(
    fieldActivities: FieldActivity[],
    fields: Field[],
    users: User[],
    products: Product[],
    crops: Crop[],
    equipments: Equipment[],
    prescriptionMaps: MapShape[] = [],
    farm?: Farm
  ): Observable<Blob> {
    const entitiesManager = new EntitiesManager();

    const taskObservables = [].concat(
      ...fieldActivities.map((activity) => {
        return activity.multipleCrops.map((crop) => {
          const fullCrop = crops.find((cr) => cr.id === crop.id);
          const field = fields.find((field) => field.id === crop.field.id);
          const tractor = (activity.equipments || [])
            .map((activityEquipment) => equipments.find((eq) => eq.id === activityEquipment.id))
            .find((eq) => this.equipmentService.isTractorType(eq.type) as boolean);

          const prescriptionMap =
            prescriptionMaps.find((map) => map.fields && (map.fields as any).includes(+field.id)) || // the prescription map for this field
            prescriptionMaps.find((map) => !map.fields || map.fields.length === 0) || // then, a prescription map without fields
            prescriptionMaps[0]; //then, the first one

          return this.activityToTaskEntity(
            activity,
            field,
            users,
            products,
            prescriptionMap,
            farm,
            fullCrop,
            tractor
          ).pipe(
            tap((taskEntity) => {
              entitiesManager.registerEntity(taskEntity);
            })
          );
        });
      })
    );

    return forkJoin(taskObservables).pipe(
      switchMap(() => entitiesManager.generateXML({ fmisTitle: FMIS_TITLE, fmisURI: FMIS_URI }))
    );
  }

  prescriptionMapToISOXML(
    prescriptionMap: MapShape,
    fields: Field[],
    farm?: Farm
  ): Observable<Blob> {
    const entitiesManager = new EntitiesManager();
    const fieldId = prescriptionMap.fields[0] as any;

    const field = fields.find((field) => field.id === fieldId);

    const activity = {
      done: false,
      type: 'feeding',
      users: [],
    };

    return this.activityToTaskEntity(activity as any, field, [], [], prescriptionMap, farm).pipe(
      switchMap((taskEntity) => {
        entitiesManager.registerEntity(taskEntity);
        return entitiesManager.generateXML({ fmisTitle: FMIS_TITLE, fmisURI: FMIS_URI });
      })
    );
  }

  /**
   * Get the URL to download the ISOXML file for a field activity
   * @param fieldActivityId The ID of the field activity
   * @param externalSource The external source of the field activity
   * @param equipmentId The ID of the equipment
   * @returns The URL to download the ISOXML file
   * @memberof ISOXMLService
   */
  getFieldActivityIsoXmlZipUrl(
    fieldActivityId: number,
    externalSource: string,
    equipmentId: number
  ): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(
      apiBaseUrl + 'api/private/v3/isoxml/fieldActivity/' + fieldActivityId,
      null,
      {
        params: {
          externalSource,
          equipmentId,
        },
      }
    );
  }

  generateZIPFilename(name: string) {
    let dateString = new Date().toISOString().replace('.', '_');
    const regex = new RegExp('\\:', 'g');
    dateString = dateString.replace(regex, '-'); // not compatible with ISO8601
    return `taskdata_${dateString}_${name}.zip`;
  }
}
